/**
 * Minimal toast provider built on @radix-ui/react-toast. Avoids shadcn's full
 * toast stack to keep the bundle small. Expose `useToast` for consumers.
 */
import * as React from 'react';
import * as Toast from '@radix-ui/react-toast';
import { cn } from '@/lib/cn';

type ToastItem = {
  id: number;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
};

type Ctx = {
  toast: (t: Omit<ToastItem, 'id'>) => void;
};

const ToastCtx = React.createContext<Ctx | null>(null);

export const useToast = (): Ctx => {
  const ctx = React.useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const nextId = React.useRef(1);

  const toast = React.useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = nextId.current++;
    setItems((prev) => [...prev, { ...t, id }]);
  }, []);

  const dismiss = (id: number): void =>
    setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastCtx.Provider value={{ toast }}>
      <Toast.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((t) => (
          <Toast.Root
            key={t.id}
            onOpenChange={(open) => !open && dismiss(t.id)}
            className={cn(
              'grid grid-cols-[auto,max-content] items-start gap-x-4 rounded-md border p-4 shadow-lg',
              'bg-background text-foreground',
              t.variant === 'destructive' && 'border-destructive text-destructive',
              t.variant === 'success' && 'border-primary',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
            )}
          >
            <div>
              <Toast.Title className="text-sm font-semibold">{t.title}</Toast.Title>
              {t.description ? (
                <Toast.Description className="text-sm text-muted-foreground">
                  {t.description}
                </Toast.Description>
              ) : null}
            </div>
            <Toast.Close
              aria-label="fechar"
              className="rounded-md px-2 text-muted-foreground hover:bg-accent"
            >
              ×
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed bottom-0 right-0 z-50 flex w-full max-w-sm flex-col gap-2 p-6 outline-none" />
      </Toast.Provider>
    </ToastCtx.Provider>
  );
};
